/**
 * Thin wrappers over the brand layer.
 *
 * Every class here is one that already exists in `brand/components.css`, was
 * previewed in all four language/theme combinations, and passed the contrast
 * audit. Nothing invents a colour, a radius or a shadow — if a component needs
 * one that does not exist, it gets designed in the brand layer first.
 */

import clsx from 'clsx';
import { MoreHorizontal, X } from 'lucide-react';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';

// ------------------------------------------------------------------ button

type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  iconOnly?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, iconOnly, block, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={clsx(
        'dio-btn',
        `dio-btn--${variant}`,
        size === 'sm' && 'dio-btn--sm',
        size === 'lg' && 'dio-btn--lg',
        iconOnly && 'dio-btn--icon',
        block && 'dio-btn--block',
        loading && 'dio-btn--loading',
        className,
      )}
      // A loading button stays focusable and keeps its accessible name; going
      // `disabled` mid-click moves focus to the body and loses the reader's place.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...rest}
    >
      {children}
    </button>
  );
});

// ------------------------------------------------------------------- field

interface FieldProps {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: ReactNode;
  errors?: string[];
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, required, hint, errors, children, className }: FieldProps) {
  const { t } = useTranslation();
  const hasError = Boolean(errors?.length);

  /*
   * A `Field` cannot render a label that points at nothing.
   *
   * `htmlFor` is easy to give a form with fixed fields and impossible to give a
   * repeated row, which is how the social-links editor ended up with three URL
   * inputs carrying no accessible name at all — a screen-reader user reached
   * three unlabelled boxes. Rather than leave that to whoever writes the next
   * repeated row, the id is generated here and handed to the control.
   */
  const generated = useId();
  const single = isValidElement<{ id?: string }>(children) ? children : null;
  const target = htmlFor ?? single?.props.id ?? (single ? generated : undefined);
  const control = single && !htmlFor && !single.props.id ? cloneElement(single, { id: generated }) : children;

  return (
    <div className={clsx('dio-field', hasError && 'dio-field--error', className)}>
      <label className="dio-label" htmlFor={target}>
        {label}
        {required && (
          <span className="dio-label__req" aria-label={t('common.required')}>
            *
          </span>
        )}
      </label>
      {control}
      {hint && !hasError && <p className="dio-hint">{hint}</p>}
      {hasError && (
        <p className="dio-error" role="alert">
          {errors?.join(' · ')}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ inputs

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={clsx('dio-input', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} className={clsx('dio-textarea', className)} {...rest} />;
  },
);

/**
 * The wrapper is not decoration: it carries the chevron.
 *
 * A mask cannot go on the `<select>` itself without hiding its own label, and
 * the chevron has to be a mask so it takes `currentColor` and follows the text
 * into dark mode. Every select goes through here, so none of them can be left
 * without one.
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <span className="dio-select-wrap">
        <select ref={ref} className={clsx('dio-select', className)} {...rest}>
          {children}
        </select>
      </span>
    );
  },
);

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

export function Switch({ checked, onChange, label, hint, disabled }: SwitchProps) {
  const id = useId();
  return (
    <div>
      <label className="dio-switch" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="dio-switch__track" aria-hidden="true">
          <span className="dio-switch__thumb" />
        </span>
        <span>{label}</span>
      </label>
      {hint && <p className="dio-hint">{hint}</p>}
    </div>
  );
}

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}

export function Checkbox({ checked, onChange, label, disabled }: CheckboxProps) {
  const id = useId();
  return (
    <label className="dio-check" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="dio-check__box" aria-hidden="true" />
      <span className="dio-check__label">{label}</span>
    </label>
  );
}

// -------------------------------------------------------------------- card

export function Card({
  title,
  description,
  actions,
  children,
  footer,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('dio-card', className)}>
      {(title || actions) && (
        <header className="dio-card__header">
          <div>
            {title && <h2 className="dio-card__title">{title}</h2>}
            {description && <p className="dio-hint">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="dio-card__body">{children}</div>
      {footer && <footer className="dio-card__footer">{footer}</footer>}
    </section>
  );
}

// ------------------------------------------------------------------- badge

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'outline';

export function Badge({
  tone = 'neutral',
  dot,
  children,
}: {
  tone?: BadgeTone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={clsx('dio-badge', tone !== 'neutral' && `dio-badge--${tone}`)}>
      {/* Status is never colour alone — the dot carries the same information
          for anyone who cannot separate the hues. */}
      {dot && <span className="dio-badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

// ------------------------------------------------------------------- alert

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'success' | 'warning' | 'danger' | 'info';
  children: ReactNode;
}) {
  return (
    <div className={clsx('dio-alert', `dio-alert--${tone}`)} role={tone === 'danger' ? 'alert' : undefined}>
      <div className="dio-alert__body">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------------- modal

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  labelledBy?: string;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // Move focus in, or a keyboard user stays behind the overlay.
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="dio-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        className="dio-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId}
        tabIndex={-1}
      >
        <header className="dio-card__header">
          <h2 id={titleId} className="dio-card__title">
            {title}
          </h2>
          <Button variant="ghost" size="sm" iconOnly onClick={onClose} aria-label={t('common.close')}>
            <X className="dio-icon" aria-hidden="true" />
          </Button>
        </header>
        <div className="dio-card__body">{children}</div>
        {footer && <footer className="dio-card__footer">{footer}</footer>}
      </div>
    </div>
  );
}

// -------------------------------------------------------------- empty/load

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="dio-empty">
      {icon && (
        <div className="dio-empty__icon" aria-hidden="true">
          {icon}
        </div>
      )}
      <p className="dio-empty__title">{title}</p>
      {hint && <p className="dio-empty__text">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3" role="status" aria-label={t('common.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="dio-skeleton dio-skeleton--text" />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ table

/**
 * The list screens' primary shape.
 *
 * Every list used to render one `Card` per record — ~90px a row, so a menu of
 * forty-four dishes took eight screens to read and the actions outweighed the
 * data. `columns` are the header cells; `children` are the `<tr>`s.
 *
 * The caption is not decoration: a table needs an accessible name, and the
 * screens each already have an `h1` that says what the list is.
 */
export function DataTable({
  caption,
  columns,
  children,
}: {
  caption: string;
  columns: ReactNode[];
  children: ReactNode;
}) {
  return (
    <div className="dio-table-wrap">
      <table className="dio-table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column, index) => (
              // Columns are a fixed list declared at the call site and never
              // reordered, so the index is a stable key.
              <th key={index} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------ action menu

export interface ActionMenuItem {
  label: ReactNode;
  onSelect: () => void;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Shown as the control's tooltip — used to say why it is disabled. */
  title?: string;
}

/**
 * A row's secondary actions, behind one trigger.
 *
 * The item list carried four buttons per record in three different treatments,
 * wrapped two by two — 176 controls on one page, all louder than the dishes.
 * Nothing is removed here: every button became an item with the same label and
 * the same handler, and Edit stays outside as a button because it is the one
 * action people repeat.
 *
 * This is a real menu, not a div that opens: `role="menu"`, roving focus on the
 * arrow keys, Escape closes and hands focus back to the trigger. A menu you can
 * open with the keyboard and not navigate is worse than no menu, because it
 * takes the actions away from the person who most needed them listed.
 */
export function ActionMenu({ items, label }: { items: ActionMenuItem[]; label?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const enabled = items.filter((item) => !item.disabled);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  }, []);

  /*
   * The menu is `position: fixed`, so it has to be told where to go.
   *
   * That is not a style preference: the row menu lives inside `.dio-table-wrap`,
   * which is a scroll container, and an absolutely-positioned descendant of one
   * is clipped by it — 139px of a 150px menu, measured, which made Publish
   * unreachable on nearly every row. Fixed leaves that containing block behind;
   * the cost is that the coordinates are ours to compute.
   *
   * These are physical (`left`/`top`) on purpose, which is the one place this
   * codebase does not use logical properties: `getBoundingClientRect` reports
   * physical pixels, so the mirroring is done here in the arithmetic rather
   * than handed to the cascade, where it would be applied twice.
   */
  const place = useCallback(() => {
    const t = trigger.current?.getBoundingClientRect();
    const el = menu.current;
    if (!t || !el) return;

    const rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    const { offsetWidth: w, offsetHeight: h } = el;
    const gap = 4;
    const edge = 8;

    // Below the trigger, flipping above when the viewport has no room — a menu
    // opened on the last row would otherwise hang off the bottom of the screen.
    let top = t.bottom + gap;
    if (top + h > window.innerHeight - edge) top = Math.max(edge, t.top - gap - h);

    // The menu's end edge lines up with the trigger's end edge, which is the
    // row's end in both directions.
    let left = rtl ? t.left : t.right - w;
    left = Math.min(Math.max(edge, left), window.innerWidth - w - edge);

    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  }, []);

  // Before paint, or the menu is visible at 0,0 for a frame.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    // Focus the first item, so the keyboard lands inside the thing that opened.
    const first = menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    first?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    // A click anywhere else dismisses it. `mousedown` rather than `click` so the
    // menu is gone before the thing under the pointer reacts.
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close(false);
    };
    // A fixed menu does not travel with the row it belongs to, so scrolling
    // would leave it pointing at nothing. Capture, because the scroll that
    // matters is the content pane's and the table's, not the document's.
    const onScroll = () => close(false);

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, close]);

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const focusable = [
      ...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []),
    ];
    if (focusable.length === 0) return;
    const current = focusable.indexOf(document.activeElement as HTMLButtonElement);

    // Arrow keys wrap. A menu is a ring, not a list with two dead ends.
    const move = (next: number) => {
      event.preventDefault();
      focusable[(next + focusable.length) % focusable.length]?.focus();
    };

    if (event.key === 'ArrowDown') move(current + 1);
    else if (event.key === 'ArrowUp') move(current - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(focusable.length - 1);
  };

  if (enabled.length === 0) return null;

  return (
    <div className="dio-actions" ref={root}>
      <Button
        ref={trigger}
        variant="ghost"
        size="sm"
        iconOnly
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label ?? t('common.moreActions')}
        onClick={() => setOpen((value) => !value)}
      >
        <MoreHorizontal className="dio-icon" aria-hidden="true" />
      </Button>

      {open && (
        <div
          id={menuId}
          ref={menu}
          className="dio-actions__menu dio-menu"
          role="menu"
          aria-label={label ?? t('common.moreActions')}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              role="menuitem"
              className={clsx('dio-menu__item', item.danger && 'dio-menu__item--danger')}
              disabled={item.disabled}
              title={item.title}
              onClick={() => {
                // Close before running: the handler may open a dialog, and two
                // layers fighting over focus is how the dialog loses it.
                close(false);
                item.onSelect();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- toolbar

export function Toolbar({ children, end }: { children?: ReactNode; end?: ReactNode }) {
  // An empty toolbar is an empty box with a border round it. The users screen
  // rendered exactly that — `<Toolbar />` with nothing in it — which was merely
  // an odd gap while this was a borderless band.
  if (!children && !end) return null;

  return (
    <div className="dio-toolbar dio-toolbar--standalone">
      {children}
      <div className="dio-toolbar__spacer" />
      {end}
    </div>
  );
}

// -------------------------------------------------------------- pagination

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <nav className="dio-pagination" aria-label={t('common.page')}>
      <span className="dio-meta tabular">{t('common.showingCount', { from, to, total })}</span>
      <button
        type="button"
        className="dio-page-btn"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
      >
        {t('common.previous')}
      </button>
      <button
        type="button"
        className="dio-page-btn"
        onClick={() => onPage(page + 1)}
        disabled={page >= lastPage}
      >
        {t('common.next')}
      </button>
    </nav>
  );
}
